/**
 * DependencyGraphV2 — Doctrine 51, refonte hiérarchique sous React Flow.
 *
 * Drop-in replacement de DependencyGraph.jsx avec exactement la même API :
 *     <DependencyGraphV2
 *        centerId={...}
 *        nodes={[{id, name, type, criticality, is_self, ...}]}
 *        edges={[{id, from_node_id, to_node_id, kind, weight}]}
 *        selectedId={...}
 *        onSelectNode={(node) => ...}
 *        height={460}
 *        testIdPrefix="graph-viz"
 *     />
 *
 * Différences vs V1 :
 *   - React Flow (@xyflow/react) au lieu de SVG custom → pan/zoom/minimap natifs
 *   - Cartes rectangulaires identiques visuellement à UtilityMapping (cohérence UX
 *     avec la "Cartographie des dépendances" que l'utilisateur a validée)
 *   - Layout hiérarchique : centre au milieu, dépendances entrantes en haut,
 *     sortantes en bas, partenaires/concurrents sur les côtés
 *   - Edges courbées avec labels lisibles + couleur par type de relation
 *   - Click sur un nœud non-central → propage onSelectNode (drill-down P4)
 *
 * Mode strictement additif : le composant V1 reste intact dans le repo.
 */
import { useMemo, useCallback, useEffect } from "react";
import {
  ReactFlow, Background, Controls, MiniMap, Handle, Position,
  useNodesState, useEdgesState,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {
  Building2, ShieldAlert, Truck, Cpu, Users, Handshake, RefreshCcw, HelpCircle, Crown,
} from "lucide-react";

// --- Catalogue des types de NODE (cohérent avec V1) ---
const TYPE_META = {
  organisation: { color: "#EF9F27", label: "Org",         Icon: Building2 },
  regulateur:   { color: "#E24B4A", label: "Régulateur",  Icon: ShieldAlert },
  fournisseur:  { color: "#7F77DD", label: "Fournisseur", Icon: Truck },
  tech:         { color: "#7F77DD", label: "Tech",        Icon: Cpu },
  client:       { color: "#1D9E75", label: "Client",      Icon: Users },
  partenaire:   { color: "#378ADD", label: "Partenaire",  Icon: Handshake },
  substitut:    { color: "#A1A1AA", label: "Substitut",   Icon: RefreshCcw },
  other:        { color: "#71717A", label: "Autre",       Icon: HelpCircle },
};

// --- Catalogue des KINDS d'arête (cohérent avec V1) ---
const KIND_META = {
  depends_on: { color: "#E24B4A", label: "dépend de",   dash: "0",   axis: "in"  },
  supplies:   { color: "#7F77DD", label: "fournit",     dash: "0",   axis: "in"  },
  regulates:  { color: "#EF4444", label: "régule",      dash: "5,3", axis: "in"  },
  partner:    { color: "#1D9E75", label: "partenaire",  dash: "0",   axis: "side"},
  competes:   { color: "#A78BFA", label: "concurrent",  dash: "3,3", axis: "side"},
  owns:       { color: "#EFD127", label: "détient",     dash: "0",   axis: "out" },
  audits:     { color: "#9F95FF", label: "audite",      dash: "4,2", axis: "in"  },
  finances:   { color: "#1D9E75", label: "finance",     dash: "0",   axis: "in"  },
};

const typeMeta = (t) => TYPE_META[(t || "").toLowerCase()] || TYPE_META.other;
const kindMeta = (k) => KIND_META[(k || "").toLowerCase()] || { color: "#52525B", label: k || "lien", dash: "0", axis: "side" };

// ============================================================================
// Custom Node component — calé sur UtilityMapping pour cohérence visuelle
// ============================================================================
function DoctrineNode({ data, selected }) {
  const meta = typeMeta(data.type);
  const Icon = meta.Icon;
  const isCenter = !!data.isCenter;
  const isSelf = !!data.is_self;
  const crit = Number(data.criticality || 0);

  // Couleur de bord : rouge si critique > 70, orange centre, sinon meta.color
  const borderColor = selected
    ? "#EF9F27"
    : isCenter
      ? "#EF9F27"
      : `${meta.color}80`;

  return (
    <div
      className="rounded-lg border-2 bg-[#0a0a0a]/90 backdrop-blur shadow-lg"
      style={{
        borderColor,
        borderWidth: isCenter ? 3 : 2,
        minWidth: isCenter ? 230 : 200,
        padding: "10px 12px",
      }}
      data-testid={`depv2-node-${data.id}`}
    >
      <Handle type="target" position={Position.Top}    style={{ background: meta.color, opacity: 0.6 }} />
      <Handle type="target" position={Position.Left}   style={{ background: meta.color, opacity: 0.6 }} />
      <Handle type="source" position={Position.Bottom} style={{ background: meta.color, opacity: 0.6 }} />
      <Handle type="source" position={Position.Right}  style={{ background: meta.color, opacity: 0.6 }} />

      <div className="flex items-start gap-2">
        <div
          className="w-8 h-8 rounded-md flex items-center justify-center shrink-0"
          style={{ background: `${meta.color}22`, border: `1px solid ${meta.color}66` }}
        >
          <Icon className="w-4 h-4" style={{ color: meta.color }} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[12px] font-semibold text-white truncate flex items-center gap-1">
            {isSelf && <Crown className="w-3 h-3 text-[#EF9F27] shrink-0" />}
            {data.name}
          </div>
          <div className="text-[10px] text-zinc-500 truncate">{meta.label}</div>
        </div>
      </div>

      <div className="flex items-center gap-1 mt-2 flex-wrap">
        {crit > 0 && (
          <span
            className="text-[9px] px-1.5 py-0.5 rounded uppercase tracking-wider font-semibold"
            style={{
              background: crit >= 70 ? "#E24B4A1A" : crit >= 40 ? "#EF9F271A" : "#71717a1A",
              color:      crit >= 70 ? "#E24B4A"   : crit >= 40 ? "#EF9F27"   : "#A1A1AA",
            }}
          >
            {crit >= 70 ? "Critique" : crit >= 40 ? "Élevée" : "Modérée"}
          </span>
        )}
        {data.substitutability != null && Number(data.substitutability) <= 30 && (
          <span className="text-[9px] px-1.5 py-0.5 rounded uppercase tracking-wider bg-[#EF9F27]/15 text-[#EF9F27] border border-[#EF9F27]/30">
            Bottleneck
          </span>
        )}
        {isCenter && !isSelf && (
          <span className="text-[9px] text-zinc-500 ml-auto">{crit}%</span>
        )}
      </div>
    </div>
  );
}

const nodeTypes = { doctrine: DoctrineNode };

// ============================================================================
// Layout helpers — répartition hiérarchique
// ============================================================================
/**
 * Compute layout positions for a center node + neighbors organized by edge axis.
 * - Center: (0, 0)
 * - axis "in"   (depends_on, regulates, supplies, audits, finances) → TOP row(s)
 * - axis "out"  (owns)                                              → BOTTOM row(s)
 * - axis "side" (partner, competes)                                 → LEFT/RIGHT
 *
 * Edges where center is the FROM (out-going) flip the axis : if center "depends_on" X,
 * then X feeds the center → X goes above.
 */
function computeLayout(centerId, rawNodes, rawEdges) {
  const center = rawNodes.find((n) => n.id === centerId);
  if (!center) return { positions: {}, byNodeKind: {} };

  // For each non-center node, find the edge that connects it to center and determine axis.
  const positions = { [centerId]: { x: 0, y: 0 } };
  const buckets = { up: [], down: [], left: [], right: [] };

  const seen = new Set([centerId]);
  for (const n of rawNodes) {
    if (n.id === centerId || seen.has(n.id)) continue;
    seen.add(n.id);
    // Find connecting edge
    const e = rawEdges.find((x) =>
      (x.from_node_id === centerId && x.to_node_id === n.id) ||
      (x.to_node_id === centerId && x.from_node_id === n.id),
    );
    const km = kindMeta(e?.kind);
    const centerIsSource = e?.from_node_id === centerId;

    // If center is the source of "depends_on" → the target is upstream (above)
    // If center is the source of "owns" → the target is downstream (below)
    let axis = km.axis;
    if (centerIsSource && axis === "in")  axis = "up";
    if (!centerIsSource && axis === "in") axis = "up";  // upstream feeds center
    if (axis === "out")  axis = "down";
    if (axis === "side") axis = "right";  // default

    // Bucket assignment
    if (axis === "up")    buckets.up.push(n);
    else if (axis === "down") buckets.down.push(n);
    else if (axis === "left") buckets.left.push(n);
    else                       buckets.right.push(n);
  }

  // Distribute within each bucket
  const COL_W = 260;
  const ROW_H = 180;
  const placeRow = (arr, y) => {
    const n = arr.length;
    if (n === 0) return;
    const startX = -((n - 1) * COL_W) / 2;
    arr.forEach((node, i) => {
      positions[node.id] = { x: startX + i * COL_W, y };
    });
  };
  const placeCol = (arr, x) => {
    const n = arr.length;
    if (n === 0) return;
    const startY = -((n - 1) * ROW_H) / 2;
    arr.forEach((node, i) => {
      positions[node.id] = { x, y: startY + i * ROW_H };
    });
  };

  placeRow(buckets.up,   -ROW_H * 1.5);
  placeRow(buckets.down,  ROW_H * 1.5);
  placeCol(buckets.left, -COL_W * 1.5);
  placeCol(buckets.right, COL_W * 1.5);

  return { positions };
}

// ============================================================================
// Main component
// ============================================================================
export default function DependencyGraphV2({
  centerId,
  nodes: rawNodes = [],
  edges: rawEdges = [],
  selectedId,
  onSelectNode,
  height = 460,
  testIdPrefix = "depv2",
}) {
  const layout = useMemo(() => computeLayout(centerId, rawNodes, rawEdges), [centerId, rawNodes, rawEdges]);

  // React Flow nodes/edges built from props + layout
  const initialRfNodes = useMemo(() => {
    return rawNodes
      .filter((n) => layout.positions[n.id])
      .map((n) => ({
        id: n.id,
        type: "doctrine",
        position: layout.positions[n.id],
        data: { ...n, isCenter: n.id === centerId },
        selected: n.id === selectedId,
      }));
  }, [rawNodes, layout, centerId, selectedId]);

  const initialRfEdges = useMemo(() => {
    return rawEdges.map((e) => {
      const km = kindMeta(e.kind);
      return {
        id: e.id,
        source: e.from_node_id,
        target: e.to_node_id,
        label: km.label,
        type: "default",  // bezier
        animated: false,
        style: {
          stroke: km.color,
          strokeWidth: 1 + (Number(e.weight || 50) / 100) * 1.8,
          strokeDasharray: km.dash !== "0" ? km.dash : undefined,
          opacity: 0.7,
        },
        labelStyle: {
          fill: km.color,
          fontSize: 10,
          fontWeight: 500,
        },
        labelBgStyle: {
          fill: "#0a0a0a",
          fillOpacity: 0.85,
        },
        labelBgPadding: [4, 2],
        labelBgBorderRadius: 3,
      };
    });
  }, [rawEdges]);

  const [rfNodes, setRfNodes, onNodesChange] = useNodesState(initialRfNodes);
  const [rfEdges, setRfEdges, onEdgesChange] = useEdgesState(initialRfEdges);

  // Re-sync when props change (expand on a new node, etc.)
  useEffect(() => { setRfNodes(initialRfNodes); }, [initialRfNodes, setRfNodes]);
  useEffect(() => { setRfEdges(initialRfEdges); }, [initialRfEdges, setRfEdges]);

  const handleNodeClick = useCallback((_event, rfNode) => {
    if (!onSelectNode) return;
    const original = rawNodes.find((n) => n.id === rfNode.id);
    if (original) onSelectNode(original);
  }, [rawNodes, onSelectNode]);

  if (!rawNodes || rawNodes.length === 0) {
    return (
      <div
        className="text-zinc-500 text-sm italic text-center py-10"
        data-testid={`${testIdPrefix}-empty`}
        style={{ height: `${height}px`, display: "flex", alignItems: "center", justifyContent: "center" }}
      >
        Aucun nœud. Importez depuis l&apos;Annuaire ou créez votre premier nœud.
      </div>
    );
  }

  return (
    <div
      className="relative w-full rounded-md overflow-hidden"
      style={{
        height: `${height}px`,
        background: "radial-gradient(ellipse at center, rgba(127,119,221,0.05) 0%, rgba(10,10,10,0) 65%)",
      }}
      data-testid={testIdPrefix}
    >
      <ReactFlow
        nodes={rfNodes}
        edges={rfEdges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeClick={handleNodeClick}
        nodeTypes={nodeTypes}
        fitView
        fitViewOptions={{ padding: 0.2, maxZoom: 1 }}
        minZoom={0.3}
        maxZoom={1.8}
        proOptions={{ hideAttribution: true }}
        defaultEdgeOptions={{ type: "default" }}
      >
        <Background gap={20} size={1} color="rgba(255,255,255,0.04)" />
        <MiniMap
          nodeColor={(n) => typeMeta(n.data?.type).color}
          nodeStrokeColor={(n) => typeMeta(n.data?.type).color}
          maskColor="rgba(0,0,0,0.7)"
          style={{ background: "#0F0F12", border: "1px solid rgba(255,255,255,0.08)" }}
          pannable
          zoomable
        />
        <Controls
          showInteractive={false}
          style={{ background: "#0F0F12", border: "1px solid rgba(255,255,255,0.08)" }}
        />
      </ReactFlow>

      {/* Légende compacte en bas, comme V1 */}
      <div className="absolute bottom-2 left-2 right-32 flex items-center gap-3 text-[10px] text-zinc-500 flex-wrap pointer-events-none">
        {Object.entries(KIND_META).slice(0, 6).map(([k, m]) => (
          <span key={k} className="inline-flex items-center gap-1 bg-[#0a0a0a]/80 px-2 py-0.5 rounded backdrop-blur">
            <span className="w-3 h-[2px]" style={{ background: m.color, borderTop: m.dash !== "0" ? `1px dashed ${m.color}` : "none" }} />
            {m.label}
          </span>
        ))}
      </div>
    </div>
  );
}
